/**
 * Floating tool palette — left side of the VTT page.
 *
 * Uses `useSyncExternalStore` against the scene store so it re-renders
 * when the active tool or tool options change, without subscribing the
 * whole canvas tree to React state changes.
 *
 * Keyboard shortcuts are wired in SceneCanvas (global, window-level) so
 * they work regardless of whether this palette has focus.
 */

import { useSyncExternalStore } from "react";
import { sceneStore, type ToolKind } from "../store/sceneStore";
import "./ToolPalette.css";

interface ToolDef {
  kind: ToolKind;
  label: string;
  shortcut: string;
  /** Simple unicode icon — no icon library for now. */
  glyph: string;
}

const TOOLS: ToolDef[] = [
  { kind: "select", label: "Select", shortcut: "V", glyph: "↖" },
  { kind: "pen", label: "Pen", shortcut: "P", glyph: "✎" },
  { kind: "rect", label: "Rectangle", shortcut: "R", glyph: "▭" },
  { kind: "ellipse", label: "Ellipse", shortcut: "O", glyph: "◯" },
  { kind: "line", label: "Line", shortcut: "L", glyph: "╱" },
  { kind: "arrow", label: "Arrow", shortcut: "A", glyph: "→" },
];

const COLORS = [
  "#f5c77a", // amber — sheet A
  "#6ac1c4", // teal — sheet B
  "#e8e6e1", // parchment white
  "#f87171", // red
  "#86efac", // green
  "#0c0a14", // deep (for dark-on-parchment maps later)
];

const WIDTHS = [1, 2, 4, 8];

export default function ToolPalette() {
  // Subscribe via the per-notify counter so React's Object.is check
  // actually sees a change. The store mutates `state` in place and
  // returns the same reference, so passing getState as the snapshot
  // would silently never re-render.
  useSyncExternalStore(
    (cb) => sceneStore.subscribe(cb),
    () => sceneStore.getNotifyVersion(),
    () => sceneStore.getNotifyVersion(),
  );
  const state = sceneStore.getState();

  return (
    <div className="tool-palette">
      <div className="tool-palette__group">
        {TOOLS.map((t) => (
          <button
            key={t.kind}
            type="button"
            className={
              "tool-palette__btn" +
              (state.tool === t.kind ? " tool-palette__btn--active" : "")
            }
            title={`${t.label} (${t.shortcut})`}
            onClick={() => sceneStore.setTool(t.kind)}
          >
            <span className="tool-palette__glyph">{t.glyph}</span>
          </button>
        ))}
      </div>

      <div className="tool-palette__divider" />

      <div className="tool-palette__group tool-palette__group--swatches">
        {COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={
              "tool-palette__swatch" +
              (state.toolOptions.strokeColor === c ? " tool-palette__swatch--active" : "")
            }
            style={{ background: c }}
            onClick={() => sceneStore.setToolOptions({ strokeColor: c })}
            title={c}
          />
        ))}
      </div>

      <div className="tool-palette__group">
        {WIDTHS.map((w) => (
          <button
            key={w}
            type="button"
            className={
              "tool-palette__btn tool-palette__btn--width" +
              (state.toolOptions.strokeWidth === w ? " tool-palette__btn--active" : "")
            }
            onClick={() => sceneStore.setToolOptions({ strokeWidth: w })}
            title={`${w}px`}
          >
            <span
              className="tool-palette__width-dot"
              style={{ width: `${w * 2 + 2}px`, height: `${w * 2 + 2}px` }}
            />
          </button>
        ))}
      </div>

      <div className="tool-palette__divider" />

      <div className="tool-palette__group">
        <button
          type="button"
          className="tool-palette__btn"
          onClick={() => sceneStore.undo()}
          disabled={!sceneStore.canUndo()}
          title="Undo (Ctrl+Z)"
        >
          ↶
        </button>
        <button
          type="button"
          className="tool-palette__btn"
          onClick={() => sceneStore.redo()}
          disabled={!sceneStore.canRedo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          ↷
        </button>
      </div>
    </div>
  );
}
